import React, { useEffect, useState, useContext } from 'react';
import {
  CircularProgress,
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Box,
  Tooltip,
  IconButton,
  Stack,
  MenuItem,
  Checkbox,
  Alert,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import { Badge } from '@mui/material';
import { Visibility, PictureAsPdf,Edit, Delete, Add, ArrowUpward, ArrowDownward  } from '@mui/icons-material';
import documentoApi from '../../api/documentoApi';
import filesApi from '../../api/filesApi';
import { useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ProtectedAction from '../../components/ProtectedAction';
import BackButtonDesktop from '../../components/BackButtonDesktop';
import { AuthContext } from '../../auth/AuthContext';
import { formatDate } from '../../utils/dateFormatter';

const extractErrorMessage = (error) => {
  if (!error) return 'Errore sconosciuto';
  if (error.response?.data) return JSON.stringify(error.response?.data);
  if (!error.response) {
    return "L'elaborazione richiede più tempo del previsto. Riprova.";
  }
  return error.message || JSON.stringify(error);
};

export default function DocumentiListPageDesktop() {
  const { user } = useContext(AuthContext);
  const [documenti, setDocumenti] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [sortField, setSortField] = useState('dataCreazione');
  const [sortDirection, setSortDirection] = useState('DESC'); // ASC o DESC
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [mergeError, setMergeError] = useState(null);
  const [merging, setMerging] = useState(false);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();
  const pageSize = 10;
  const DOCUMENT_TYPES = [
    'MULTA',
    'RATEIZZAZIONE_ADE',
    'ROTTAMAZIONE',
    'AMA',
    'ACCERTAMENTO_TARI',
    'F24',
    'FF24PREDETERMINATO',
    'TARI',
    'INTIMAZIONEPAG_AGRISC',
    'BOLLOAUTO',
    'INARCASSA',
    'FATTURA_PASSIVA',
    'FATTURA_ATTIVA',
    ''
  ];
  console.log("DocumentiListPage user",user)
  useEffect(() => fetchDocumenti(0), [sortField, sortDirection]);

  const fetchDocumenti = (page) => {
    setLoading(true);
    const payload = { ...filters };
    if (payload.from) payload.from = new Date(payload.from).toISOString();
    if (payload.to) payload.to = new Date(payload.to).toISOString();
    const params = {page, size: pageSize, sort: `${sortField},${sortDirection}`};
    if (user.role === 'ADMIN') {
      documentoApi.list(payload, page, pageSize, `${sortField},${sortDirection}`)
        .then(r => { setDocumenti(r.data.content || [])
                         setTotalPages(r.data.totalPages || 0);
                         setPage(r.data.number || 0);
         })
        .catch(err => console.error(err)).finally(setLoading(false));
    } else if (user.role === 'STUDIO') {
      documentoApi.listbystudio(user.studioId, params)
        .then(r => { setDocumenti(r.data.content || [])
                     setTotalPages(r.data.totalPages || 0);
                     setPage(r.data.number || 0);
      })
        .catch(err => console.error(err)).finally(setLoading(false));
    } else if (user.role === 'CONTRIBUENTE') {
      documentoApi.listbycontribuente(user.contribuenteId, payload, page, pageSize, `${sortField},${sortDirection}`)
        .then(r => { setDocumenti(r.data.content || [])
                     setTotalPages(r.data.totalPages || 0);
                     setPage(r.data.number || 0);
         })
        .catch(err => console.error(err)).finally(setLoading(false));
    }
    setPage(page);
    setLoading(false);
  };

  const openDeleteDialog = (id) => {
    setSelectedId(id);
    setOpenDeleteModal(true);
  };
  const closeDeleteDialog = () => {
    setSelectedId(null);
    setOpenDeleteModal(false);
  };
  const confirmDelete = async () => {
    try { await documentoApi.delete(selectedId); fetchDocumenti(page); }
    catch (err) { setErr(extractErrorMessage(err)); }
    closeDeleteDialog();
  };
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'ASC' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

    const handleDownload = async (documentoId, fileName) => {
      try {
        setLoading(true);
        const response = await documentoApi.downloadDocumento(documentoId);
        const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error(err);
         setErr(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
      // --- SELEZIONE E MERGE ---
      const toggleSelection = (id) => {
        setSelectedDocumentIds(prev =>
          prev.includes(id)
            ? prev.filter(x => x !== id)
            : [...prev, id]
        );
      };
      const canMerge = () => {
        if (selectedDocumentIds.length < 2) return false;

        const selectedDocs = documenti.filter(d =>
          selectedDocumentIds.includes(d.id)
        );

        const first = selectedDocs[0];
        return selectedDocs.every(d =>
          d.contribuente.id === first.contribuente.id &&
          d.tipo === first.tipo &&
          d.numeroDocumento === first.numeroDocumento
        );
      };

        const handleMerge = async () => {
          setMergeError(null);
          try {
            setMerging(true);
            setLoading(true);
            await documentoApi.mergeDocumenti(selectedDocumentIds);
            setSelectedDocumentIds([]);
            fetchDocumenti(page);
          } catch (e) {
            setMergeError(e.response?.data?.error || e.response?.data || 'Errore durante il merge dei documenti');
                        if(
                        e.response?.data?.error!=="I documenti non appartengono allo stesso contribuente" &&
                        e.response?.data?.error!=="Tipologia dei documenti non compatibile" &&
                        e.response?.data?.error!=="I documento non hanno lo stesso identificativo di atto/pratica" &&
                        e.response?.data?.error!=="Servono almeno due documenti"){
                                 setSelectedDocumentIds([]);
                                 fetchDocumenti(page);
                        }

          } finally {
            setMerging(false);
            setLoading(false);
          }
        };

        // --- /SELEZIONE E MERGE ---

      const handleFilterChange = (e) => {
        const { name, value } = e.target;
        console.log("name",name);
        console.log("value",value);
        setFilters(prev => ({ ...prev, [name]: value }));
      };

      const applyFilters = () => {
        setPage(0);
        fetchDocumenti(page);
      };

      const clearFilters = () => {
        setFilters({});
        applyFilters();
      };
  return (
    <Container>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={48}/>
        </Box>
      )}
                <BackButtonDesktop />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Documenti</Typography>

      </Stack>
       {/* Filtri */}
       <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
         <TextField label="Numero documento" name="numeroDocumento" value={filters.numeroDocumento || ''} onChange={handleFilterChange} size="small"/>

         <FormControl size="small" sx={{ minWidth: 140 }}>
           <InputLabel>Tipo</InputLabel>
           <Select
             name="tipo"
             value={filters.tipo || ''}
             onChange={handleFilterChange}
             label="Tipo"
           >
             <MenuItem value=""><em>None</em></MenuItem>
             {DOCUMENT_TYPES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
           </Select>
         </FormControl>

         <TextField
           label="Creato da"
           name="from"
           type="date"
           InputLabelProps={{ shrink: true }}
           value={filters.from || ''}
           onChange={handleFilterChange}
           size="small"
         />
         <TextField
           label="A"
           name="to"
           type="date"
           InputLabelProps={{ shrink: true }}
           value={filters.to || ''}
           onChange={handleFilterChange}
           size="small"
         />
         <Button variant="contained" onClick={applyFilters}>Cerca</Button>
         <Button variant="outlined" onClick={clearFilters}>Resetta Filtri</Button>
       </Stack>
  <Stack direction="row" spacing={1}>
    {/* Bottone per creare manualmente un documento */}
    {/* <ProtectedAction user={user} contractState={user.statoContratto}>
    <Button variant="contained" color="primary" onClick={() => navigate('/documenti/create')}>
      Inserisci Documento
    </Button></ProtectedAction>*/}
    {user.role=="CONTRIBUENTE" && (
    <ProtectedAction user={user} contractState={user.statoContratto}>
        <Button variant="outlined" color="secondary" onClick={() => navigate(`/contribuenti/${user.contribuenteId}/documenti/upload`)}>
          Upload PDF
        </Button></ProtectedAction>
     )}
  </Stack>
        {/* Bottone merge */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <ProtectedAction user={user} contractState={user.statoContratto}>
            <Button
              variant="contained"
              color="warning"
              disabled={!canMerge() || merging}
              onClick={handleMerge}
            >
              Unisci documenti selezionati
            </Button>
          </ProtectedAction>
        </Stack>

        {mergeError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {typeof mergeError==='string' ? mergeError : JSON.stringify(mergeError)}
          </Alert>
        )}
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {Array.from({ length: totalPages }, (_, i) => (
          <Button
            key={i}
            variant={i === page ? "contained" : "outlined"}
            onClick={() => fetchDocumenti(i)}
          >
            {i + 1}
          </Button>
        ))}
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell onClick={() => handleSort('tipo')} style={{ cursor: 'pointer' }}>
              Tipo {renderSortIcon('tipo')}
            </TableCell>
              <TableCell  onClick={() => handleSort('numeroDocumento')} style={{ cursor: 'pointer' }}>
                Numero documento {renderSortIcon('numeroDocumento')}
              </TableCell>
            <TableCell >
                 Contribuente
            </TableCell>
            <TableCell >
              Rate saltate
            </TableCell>
            <TableCell >
              Decaduto
            </TableCell>
            <TableCell onClick={() => handleSort('dataCreazione')} style={{ cursor: 'pointer' }}>
              Data Creazione {renderSortIcon('dataCreazione')}
            </TableCell>
            <TableCell onClick={() => handleSort('dataDocumento')} style={{ cursor: 'pointer' }}>
              Data Documento {renderSortIcon('dataDocumento')}
            </TableCell>
            <TableCell>Azioni</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documenti.map(d => (
            <TableRow key={d.id}>
              <TableCell>{d.tipo}</TableCell>
              <TableCell>{d.numeroDocumento}</TableCell>
        <TableCell>{d.contribuente.cognome} {d.contribuente.nome}</TableCell>
      <TableCell>
        {d.rateSaltate > 0 ? (
          <Badge color="error" badgeContent={d.rateSaltate}>
            <span></span>
          </Badge>
        ) : (
          <span>{d.rateSaltate}</span>
        )}
      </TableCell>
      <TableCell>{d.decaduta?'SI':'NO'}</TableCell>
      <TableCell>{formatDate(d.dataCreazione)}</TableCell>
      <TableCell>{formatDate(d.dataDocumento)}</TableCell>
              <TableCell>
                <Stack direction="row">
                <Tooltip title="dettaglio">
                  <IconButton color="primary" size="small" onClick={() => navigate(`/documenti/${d.id}`)}><Visibility /></IconButton>
                 </Tooltip>
                  <ProtectedAction user={user} contractState={user.statoContratto}>
                  <Tooltip title="modifica">
                  <IconButton color="secondary" size="small" onClick={() => navigate(`/documenti/edit/${d.id}`)}><Edit /></IconButton>
               </Tooltip>
                  <Tooltip title="elimina">
                  <IconButton color="error" size="small" onClick={() => openDeleteDialog(d.id)}><Delete /></IconButton>
                </Tooltip>
                   <Tooltip title="seleziona per merge">
                            <Checkbox
                              checked={selectedDocumentIds.includes(d.id)}
                              onChange={() => toggleSelection(d.id)}
                            />
                   </Tooltip>
                  </ProtectedAction>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {Array.from({ length: totalPages }, (_, i) => (
          <Button
            key={i}
            variant={i === page ? "contained" : "outlined"}
            onClick={() => fetchDocumenti(i)}
          >
            {i + 1}
          </Button>
        ))}
      </Stack>

      <ConfirmDeleteModal
        open={openDeleteModal}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
        message="Sei sicuro di voler eliminare questo documento?"
      />
    </Container>
  );
}
